import React from 'react';

import { useCurrentUser } from 'Auth';

import {
  useCreate,
  useUpdate,
  useSmartRule,
  useIsUpdating,
} from 'scenes/SmartRules/data';

import {
  OWNERS,
  TRAFFIC_TYPE,
  TRAFFIC_TYPE_ADVERTISING,
} from './AdvertisingPopulation';
import { GENERAL, NAME, AFFECTED_LEVEL } from './General';
import { NOTIFICATIONS } from './Notifications';
import { PERIODS, periodsValidationSchema } from './Periods';
import { SCHEDULE, scheduleSchema } from './Schedule';
import SmartRuleForm from './SmartRuleForm';
import { ADVERTISING_TARGET, AFFECTED_LEVELS, OWNER } from './constants';

const createDefaultInitialValues = {
  [GENERAL]: {
    [AFFECTED_LEVEL]: AFFECTED_LEVELS.AFFILIATE,
  },
  [PERIODS]: periodsValidationSchema.default(),
  [SCHEDULE]: scheduleSchema.default(),
};

const normalizeSubmittedData = data => {
  const {
    schedule: { runAtTime, runOnceDate, ...schedule },
  } = data;

  const [runAtHours, runAtMinutes] = runAtTime?.split(':') || [];
  const dateRunOnceDate = new Date(runOnceDate);
  const date = new Date();
  const dateForTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseFloat(runAtHours),
    parseFloat(runAtMinutes)
  );

  return {
    ...data,
    schedule: {
      ...schedule,
      ...(runOnceDate && runAtHours && runAtMinutes
        ? {
            runOnceDate: new Date(
              dateRunOnceDate.getFullYear(),
              dateRunOnceDate.getMonth(),
              dateRunOnceDate.getDate(),
              runAtHours,
              runAtMinutes
            ).toISOString(),
          }
        : {
            runAtHours: dateForTime.getUTCHours(),
            runAtMinutes: dateForTime.getUTCMinutes(),
          }),
    },
  };
};

export const SmartRuleFormCreate = ({ onSubmit, onClose, onExited, open }) => {
  const [create] = useCreate();
  const isUpdating = useIsUpdating();
  const currentUser = useCurrentUser();

  const handleSubmit = async values => {
    const response = await create(normalizeSubmittedData(values));
    onSubmit(response);
  };

  return (
    <SmartRuleForm
      title={'Add Smart Rule'}
      loading={isUpdating}
      onClose={onClose}
      initialValues={{
        ...createDefaultInitialValues,
        [ADVERTISING_TARGET]: {
          [OWNERS]: [currentUser.id],
          [TRAFFIC_TYPE]: TRAFFIC_TYPE_ADVERTISING,
        },
        [NOTIFICATIONS]: [
          {
            userId: currentUser.id,
            login: currentUser.login,
            name: currentUser.name,
            userEmail: currentUser.email,
          },
        ],
      }}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
    />
  );
};

const numberWithZero = i => {
  return `${i.toString().padStart(2, '0')}`;
};

const atTime = dateObj => {
  return `${numberWithZero(dateObj.getHours())}:${numberWithZero(
    dateObj.getMinutes()
  )}`;
};

const normalizeEditableData = data => {
  if (!data) {
    return {};
  }

  const {
    schedule: { runOnceDate, runAtHours, runAtMinutes, ...schedule },
  } = data;

  const runOnceDateUTC = new Date(runOnceDate);
  const runOnceDateLocal = new Date(
    Date.UTC(
      runOnceDateUTC.getUTCFullYear(),
      runOnceDateUTC.getUTCMonth(),
      runOnceDateUTC.getUTCDate(),
      runOnceDateUTC.getUTCHours(),
      runOnceDateUTC.getUTCMinutes()
    )
  );
  const date = new Date();
  const dateForTime = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      runAtHours,
      runAtMinutes
    )
  );

  return {
    ...data,
    schedule: {
      ...schedule,
      runOnceDate: runOnceDate ? runOnceDateLocal.toISOString() : undefined,
      runAtTime: atTime(runOnceDate ? runOnceDateLocal : dateForTime),
    },
  };
};

export const SmartRuleFormEdit = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const { data, isLoading } = useSmartRule({ id });
  const [update, { isLoading: isUpdating }] = useUpdate();

  const handleSubmit = async values => {
    const createConceptFamilyId = await update(normalizeSubmittedData(values));
    onSubmit({ id: createConceptFamilyId });
  };

  const initialValues = normalizeEditableData(data);

  return (
    <SmartRuleForm
      loading={isLoading || isUpdating}
      title={'Edit Smart Rule'}
      subTitle={initialValues?.[GENERAL]?.[NAME]}
      onClose={onClose}
      active={!!data}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isEdit={true}
      entityId={id}
    />
  );
};

const normalizeDuplicateData = data => {
  const normalizedData = normalizeEditableData(data);
  return {
    ...normalizedData,
    ...(normalizedData?.[GENERAL]
      ? {
          [GENERAL]: {
            ...normalizedData[GENERAL],
            [NAME]: `${normalizedData[GENERAL][NAME]}-copy`,
          },
        }
      : null),
  };
};

export const SmartRuleFormDuplicate = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const { data, isLoading } = useSmartRule({ id });
  const isUpdating = useIsUpdating();
  const currentUser = useCurrentUser();
  const [create] = useCreate();

  const { [OWNER]: owner, ...initialValues } = normalizeDuplicateData(data);

  const handleSubmit = async values => {
    const response = await create(normalizeSubmittedData(values));
    onSubmit(response);
  };

  return (
    <SmartRuleForm
      title={'Add Smart Rule'}
      loading={isLoading || isUpdating}
      onClose={onClose}
      initialValues={{
        ...initialValues,
        [NOTIFICATIONS]: [
          ...(initialValues?.[NOTIFICATIONS]?.some(
            user => user.userId === currentUser.id
          )
            ? []
            : [
                {
                  userId: currentUser.id,
                  login: currentUser.login,
                  name: currentUser.name,
                  userEmail: currentUser.email,
                },
              ]),
          ...(initialValues?.[NOTIFICATIONS]?.filter(
            user =>
              user.userId !== owner.userId || user.userId === currentUser.id
          ) || []),
        ],
      }}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
    />
  );
};
